import { defaultParseSearch, Outlet, useLocation, useRouter } from '@tanstack/react-router';
import { AnimatePresence, motion, type Variants } from 'framer-motion';
import {
  ArrowRightLeft,
  ChevronRight,
  FileStack,
  Landmark,
  LineChart,
  MessageCircleQuestion,
  PieChart,
  Receipt,
  Search,
  ShieldCheck,
  Store,
  UsersRound,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Suspense, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useTokenStore } from '@shared/auth/token.store';
import { useAuth } from '@shared/auth/use-auth';
import { BrandLogo } from '@shared/components/branding/BrandLogo';
import { ModuleErrorBoundary } from '@shared/components/feedback/ModuleErrorBoundary';
import { PageSkeleton } from '@shared/components/feedback/PageSkeleton';
import { Spinner } from '@shared/components/feedback/Spinner';
import { ImpersonationBanner } from '@shared/components/layout/ImpersonationBanner';
import { SearchModal } from '@shared/components/layout/SearchModal';
import { SidebarUserMenu } from '@shared/components/layout/SidebarUserMenu';
import { StartMenu } from '@shared/components/layout/StartMenu';
import { TabsBar } from '@shared/components/layout/TabsBar';
import { cn } from '@shared/components/ui/cn';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from '@shared/components/ui/sidebar';
import { SECURITY_ROLES, hasAnyRole } from '@shared/guards/require-role';
import { useHotkeys } from '@shared/hooks/use-hotkeys';
import { useModuleFlags } from '@shared/hooks/use-module-flags';
import { useTenantFavicon } from '@shared/hooks/use-tenant-favicon';
import { pageVariants, pageTransition } from '@shared/lib/animations';
import { ROUTE_I18N_KEYS } from '@shared/lib/route-i18n-keys';
import { useTabsStore } from '@shared/stores/tabs.store';
import { AI_PANEL_DEFAULT_WIDTH, useUIStore } from '@shared/stores/ui.store';

// ── Types ─────────────────────────────────────────────────────────────────────

interface NavSubItem {
  to: string;
  /** Server-side module key — see `flag` on NavItem. */
  flag?: string;
}

interface NavItem {
  to: string;
  icon: LucideIcon;
  children?: NavSubItem[];
  /**
   * The server's module key for this entry (`crm.vault`, `crm.invoices`, …).
   * When the tenant has it switched off, every query behind the route 403s,
   * so the entry is dropped rather than offered — see useModuleFlags.
   *
   * These keys are the backend's ALL_MODULE_FLAGS, NOT this app's route paths;
   * the two do not line up (`/wealth/investors` is `crm.wealthManagement.investors`),
   * which is why the mapping is spelled out per entry instead of derived.
   */
  flag?: string;
  /**
   * Optional gate, evaluated on every render. Only set where a module is
   * genuinely role-restricted — most entries are open to any signed-in user,
   * and gating them all on the client's `Permission` strings would hide
   * everything, since nothing ever populates them.
   */
  visibleWhen?: () => boolean;
}

// ── Nav structure (per spec) ──────────────────────────────────────────────────

// Tasks/Calendar/Messenger/Administration/Marketing temporarily removed from nav.
const CRM_NAV: NavItem[] = [
  { to: '/dashboard', icon: PieChart, flag: 'crm.dashboard' },
  { to: '/analytics', icon: LineChart, flag: 'crm.analytics' },
  { to: '/wealth/investors', icon: UsersRound, flag: 'crm.wealthManagement.investors' },
  { to: '/wealth/accounts', icon: Landmark, flag: 'crm.wealthManagement.accounts' },
  { to: '/transactions', icon: ArrowRightLeft, flag: 'crm.wealthManagement.transactions' },
  { to: '/client-requests', icon: MessageCircleQuestion, flag: 'crm.clientRequests' },
  { to: '/marketplace', icon: Store, flag: 'crm.wealthManagement.marketplace' },
  { to: '/invoices', icon: Receipt, flag: 'crm.invoices' },
  {
    to: '/security',
    icon: ShieldCheck,
    flag: 'crm.security',
    // Spec: "A regular user has no entry point to the module." The routes carry
    // the same gate, so this hides the affordance rather than being the control.
    visibleWhen: () => hasAnyRole(SECURITY_ROLES),
    children: [
      { to: '/security/users', flag: 'crm.settings.usersAndRoles' },
      { to: '/security/roles', flag: 'crm.settings.usersAndRoles' },
      { to: '/security/logs', flag: 'crm.settings.securityAudit' },
      { to: '/security/sessions', flag: 'crm.settings.securityAudit' },
    ],
  },
  { to: '/vault', icon: FileStack, flag: 'crm.vault' },
];

// ── Sidebar stagger animation ─────────────────────────────────────────────────

const sidebarVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.025, delayChildren: 0.06 } },
};

const sidebarItemVariants: Variants = {
  hidden: { opacity: 0, x: -6 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.15, ease: 'easeOut' as const },
  },
};

// ── CollapsibleNavItem ────────────────────────────────────────────────────────

interface CollapsibleNavItemProps {
  item: NavItem;
  pathname: string;
  onNavigate: (path: string) => void;
}

function CollapsibleNavItem({ item, pathname, onNavigate }: CollapsibleNavItemProps) {
  const { t } = useTranslation();
  const isChildActive = item.children?.some((c) => pathname.startsWith(c.to)) ?? false;
  const [open, setOpen] = useState(isChildActive);

  useEffect(() => {
    if (isChildActive) setOpen(true);
  }, [isChildActive]);
  const isActive = pathname === item.to || pathname.startsWith(`${item.to}/`);

  const label = t(ROUTE_I18N_KEYS[item.to] ?? item.to);

  return (
    <SidebarMenuItem>
      <motion.div variants={sidebarItemVariants}>
        <SidebarMenuButton
          tooltip={label}
          isActive={isActive && !open}
          onClick={() => {
            setOpen((prev) => !prev);
          }}
        >
          <item.icon />
          <span>{label}</span>
          <ChevronRight
            className={cn(
              'ms-auto size-4 shrink-0 transition-transform duration-200',
              open ? 'rotate-90' : 'rtl:-scale-x-100',
            )}
          />
        </SidebarMenuButton>

        {item.children && item.children.length > 0 && (
          <div
            className={cn(
              'grid transition-[grid-template-rows] duration-200 ease-in-out',
              open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
            )}
          >
            <div className="overflow-hidden" inert={!open}>
              <SidebarMenuSub>
                {item.children.map((child) => (
                  <SidebarMenuSubItem key={child.to}>
                    <SidebarMenuSubButton
                      isActive={pathname === child.to || pathname.startsWith(`${child.to}/`)}
                      href={child.to}
                      onClick={(e) => {
                        // Let the browser handle modified clicks (new tab/window).
                        if (
                          e.defaultPrevented ||
                          e.button !== 0 ||
                          e.metaKey ||
                          e.ctrlKey ||
                          e.shiftKey ||
                          e.altKey
                        ) {
                          return;
                        }
                        e.preventDefault();
                        onNavigate(child.to);
                      }}
                    >
                      {t(ROUTE_I18N_KEYS[child.to] ?? child.to)}
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </div>
          </div>
        )}
      </motion.div>
    </SidebarMenuItem>
  );
}

// ── AppShell ──────────────────────────────────────────────────────────────────

export interface AppShellProps {
  /** AI CIO floating panel overlay, injected by the app layer (boundaries). */
  aiPanel?: ReactNode;
  /** AI CIO trigger button rendered in the top-right action cluster. */
  aiTrigger?: ReactNode;
}

export function AppShell({ aiPanel, aiTrigger }: AppShellProps = {}) {
  const { isLoading, isLoggingOut } = useAuth();

  return (
    <AnimatePresence mode="wait" initial={false}>
      {isLoggingOut ? (
        <motion.div
          key="logging-out"
          className="flex h-svh w-full items-center justify-center bg-background"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <Spinner delayMs={0} />
        </motion.div>
      ) : isLoading ? (
        <motion.div
          key="loading"
          className="min-h-svh w-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <PageSkeleton />
        </motion.div>
      ) : (
        <motion.div
          key="app"
          className="flex h-svh w-full overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          <AppShellInner aiPanel={aiPanel} aiTrigger={aiTrigger} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── AppShellInner ─────────────────────────────────────────────────────────────

function AppShellInner({ aiPanel, aiTrigger }: AppShellProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const router = useRouter();
  const { logout } = useAuth();
  useTenantFavicon();
  const {
    toggleSearch,
    toggleAiPanel,
    openAiPanel,
    aiPanelOpen,
    sidebarCollapsed,
    setSidebarCollapsed,
    toggleSidebar,
    toggleStartMenu,
    requestNewAiCioChat,
  } = useUIStore();

  // Nav entries with a `visibleWhen` gate are re-evaluated whenever the access
  // token changes, since that is where the role claim lives — subscribing to the
  // token is what makes the Security entry appear/disappear on login and logout
  // rather than only on a full remount.
  const accessToken = useTokenStore((state) => state.accessToken?.token);
  // Modules the tenant has switched off. Dropping them here is not the access
  // control -- the server refuses the queries either way -- it stops the sidebar
  // advertising a section that answers with an error the moment it is clicked.
  const { disabled: disabledModules } = useModuleFlags();
  const visibleNav = useMemo(
    () =>
      CRM_NAV.filter((item) => item.visibleWhen?.() ?? true)
        .filter((item) => !(item.flag && disabledModules.has(item.flag)))
        .map((item) =>
          item.children
            ? {
                ...item,
                children: item.children.filter(
                  (child) => !(child.flag && disabledModules.has(child.flag)),
                ),
              }
            : item,
        )
        // A group whose every child is switched off has nothing left to open.
        .filter((item) => !item.children || item.children.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the gates read the token imperatively
    [accessToken, disabledModules],
  );

  const onAiCioMainPage = location.pathname === '/ai-cio';

  // Per the "AI CIO Navigation and Behavior" spec: the panel docks at its
  // DEFAULT width (page reserves exactly that much, and per the resizing
  // rules this floor never shrinks while the panel is open). Only width
  // beyond the default — dragged wider, up to the 1/3 cap — is a pure
  // overlay on top of the page ("as the panel expands, it visually overlays
  // the current page"); the page's reserve never grows past the default.
  // DEV-3794: the panel no longer locks the rest of the page — both the
  // panel and the page stay interactive at the same time.
  const aiPanelDocked = aiPanelOpen && !onAiCioMainPage;
  const aiReserve = aiPanelDocked ? AI_PANEL_DEFAULT_WIDTH : 0;
  const { syncActiveTab, openNewTab, closeTab, activeTabId, reopenLastClosed, rememberSearch } =
    useTabsStore();

  const labelForPath = useCallback(
    (path: string): string => {
      const pathname = path.split('?')[0] ?? path;
      const key = ROUTE_I18N_KEYS[pathname];
      return key ? t(key) : (pathname.split('/').filter(Boolean).pop() ?? t('nav.page'));
    },
    [t],
  );

  // Keep the tab strip in sync with the current page. `syncActiveTab` repoints
  // the current tab in place (never spawns one). Duplicate paths are allowed
  // (e.g. two Dashboard tabs via ⌘E). Seeds a tab when there are none (first
  // load, or after "Close All"). New tabs are created only by ⌘E / New Tab.
  // Tab identity = full href (pathname + search, e.g. filters or the AI CIO
  // chat id ?c=) — DEV-4143: each tab must keep its own filter state, and a
  // path-only identity was discarding it on every switch.
  const activeHref = location.pathname + location.searchStr;

  useEffect(() => {
    syncActiveTab(activeHref, labelForPath(activeHref));
    // A bare-path navigation (left nav, search, …) has nowhere to source
    // search params from — remember the current ones per pathname so it can
    // restore them instead of resetting to defaults (DEV-4143 Case 1).
    rememberSearch(location.pathname, location.searchStr.replace(/^\?/, ''));
  }, [
    activeHref,
    syncActiveTab,
    labelForPath,
    rememberSearch,
    location.pathname,
    location.searchStr,
  ]);

  // Navigate within the current tab; href may carry search params (e.g. the
  // AI CIO chat id ?c=, or ?create=1 for the Accounts creation panel). A bare
  // path (no "?") restores that section's last-known search instead of
  // resetting it — see `rememberSearch` above.
  function navTo(href: string) {
    const [pathname, query] = href.split('?');
    const resolvedQuery = query ?? useTabsStore.getState().lastSearchByPath[pathname ?? ''];
    // Match the router's own search codec (JSON-per-value) instead of a plain
    // URLSearchParams split, which would hand back e.g. `statuses: '["NEW"]'`
    // (a string) where the route's schema expects `statuses: ['NEW']`.
    const search = resolvedQuery ? defaultParseSearch(resolvedQuery) : {};
    void router.navigate({ to: (pathname ?? '/') as '/', search: search as never });
  }

  // Explicitly open a brand-new blank tab (Start → New Tab and ⌘E only).
  function newTab() {
    openNewTab();
    navTo('/new-tab');
  }

  // ── Keyboard shortcuts ──────────────────────────────────────────────────────
  useHotkeys([
    // Search: Cmd/Ctrl+F
    { key: 'f', mod: true, handler: toggleSearch, allowInInput: false },
    // New Tab: Cmd/Ctrl+E (⌘T/⌘W/⌘⇧T are browser-reserved and uncancelable).
    // shift:false so ⌘⇧E (reopen, below) isn't matched here first.
    {
      key: 'e',
      mod: true,
      shift: false,
      handler: () => {
        newTab();
      },
    },
    // Close Tab: Cmd/Ctrl+Backspace (⌘W is browser-reserved).
    {
      key: 'backspace',
      mod: true,
      handler: () => {
        const path = closeTab(activeTabId);
        if (path) navTo(path);
      },
    },
    // Reopen closed tab: Cmd/Ctrl+Shift+E (⌘⇧T is browser-reserved).
    {
      key: 'e',
      mod: true,
      shift: true,
      handler: () => {
        const path = reopenLastClosed();
        if (path) navTo(path);
      },
    },
    // Toggle AI Panel: Cmd/Ctrl+J (and the Cmd/Ctrl+Shift+A alias per spec)
    { key: 'j', mod: true, handler: toggleAiPanel },
    { key: 'a', mod: true, shift: true, handler: toggleAiPanel },
    // New Chat: Cmd/Ctrl+K — start a fresh AI CIO chat, surfacing it in the
    // panel when no AI CIO surface is currently visible.
    {
      key: 'k',
      mod: true,
      handler: () => {
        requestNewAiCioChat();
        if (location.pathname !== '/ai-cio' && !useUIStore.getState().aiPanelOpen) {
          openAiPanel();
        }
      },
    },
    // Toggle Sidebar: Cmd/Ctrl+B or Cmd/Ctrl+\
    { key: 'b', mod: true, handler: toggleSidebar },
    { key: '\\', mod: true, handler: toggleSidebar },
    // Quick Create: C (no modifier) opens the "+" Start menu when no input is
    // focused (New Chat / Task / Event / Investor / New Tab), per the spec.
    { key: 'c', handler: toggleStartMenu },
    {
      key: 'l',
      mod: true,
      shift: true,
      handler: () => {
        void logout();
      },
    },
    // Escape: close search / AI panel. Reports "unhandled" when neither is open so the event
    // isn't marked as consumed and a page's own side panel can still close on Escape.
    {
      key: 'Escape',
      handler: () => {
        const { searchOpen, aiPanelOpen } = useUIStore.getState();
        if (!searchOpen && !aiPanelOpen) return false;
        if (searchOpen) useUIStore.getState().toggleSearch();
        if (aiPanelOpen) useUIStore.getState().closeAiPanel();
      },
      allowInInput: true,
    },
  ]);

  // Opening the AI CIO floating panel auto-collapses the main nav (per spec) —
  // a one-time action on the rising edge of `aiPanelOpen`, not a standing lock:
  // the user can still manually re-expand the sidebar afterwards while the
  // panel stays open (previously `open`/`onOpenChange` below hard-forced
  // collapsed and ignored toggles for as long as the panel was open, so the
  // sidebar trigger/hotkey silently did nothing while a chat was active).
  // Closing the panel is the symmetric falling edge: restore whatever
  // collapsed/expanded state the sidebar was in right before it auto-collapsed
  // (DEV-3776 — previously nothing reopened the sidebar on close).
  const wasAiPanelOpenRef = useRef(false);
  const sidebarCollapsedBeforePanelRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (aiPanelOpen && !wasAiPanelOpenRef.current) {
      sidebarCollapsedBeforePanelRef.current = useUIStore.getState().sidebarCollapsed;
      setSidebarCollapsed(true);
    } else if (!aiPanelOpen && wasAiPanelOpenRef.current) {
      if (sidebarCollapsedBeforePanelRef.current !== null) {
        setSidebarCollapsed(sidebarCollapsedBeforePanelRef.current);
        sidebarCollapsedBeforePanelRef.current = null;
      }
    }
    wasAiPanelOpenRef.current = aiPanelOpen;
  }, [aiPanelOpen, setSidebarCollapsed]);

  return (
    <SidebarProvider
      open={!sidebarCollapsed}
      onOpenChange={(next) => {
        setSidebarCollapsed(!next);
      }}
    >
      {/* ── Left sidebar ─────────────────────────────────────────────── */}
      <Sidebar collapsible="icon" hoverExpand>
        {/* Header: logo + toggle (collapsed: only toggle). No bottom border and
            no wrapper padding — the inner 49px row is self-contained, so the
            logo flows straight into the nav at the design's spacing. */}
        <SidebarHeader className="p-0">
          <div className="flex h-[49px] items-center justify-between px-4 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
            <button
              onClick={() => {
                // Per spec: clicking the company logo opens the Main Chatbot
                // page as a NEW chat — within the current tab (no new tab).
                requestNewAiCioChat();
                navTo('/ai-cio');
              }}
              className="group-data-[collapsible=icon]:hidden text-start"
              title={t('sidebar.openAiCio', 'Open AI CIO')}
            >
              <motion.span
                className="block text-foreground"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.05 }}
              >
                <BrandLogo />
              </motion.span>
            </button>
            <SidebarTrigger className="text-sidebar-foreground/60 hover:text-sidebar-foreground" />
          </div>
        </SidebarHeader>

        <SidebarContent className="overflow-y-auto overflow-x-hidden">
          {/* ── Quick actions ─────────────────────────────────────────── */}
          <SidebarGroup>
            <SidebarGroupContent>
              <motion.div variants={sidebarVariants} initial="hidden" animate="visible">
                <SidebarMenu>
                  <SidebarMenuItem>
                    <motion.div variants={sidebarItemVariants}>
                      <StartMenu onNavigate={navTo} onNewTab={newTab} />
                    </motion.div>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <motion.div variants={sidebarItemVariants}>
                      <SidebarMenuButton tooltip={t('actions.search')} onClick={toggleSearch}>
                        <Search />
                        <span>{t('actions.search')}</span>
                      </SidebarMenuButton>
                    </motion.div>
                  </SidebarMenuItem>
                </SidebarMenu>
              </motion.div>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* ── CRM navigation ─────────────────────────────────────────── */}
          <SidebarGroup>
            <SidebarGroupLabel>{t('sidebar.crm', 'CRM')}</SidebarGroupLabel>
            <SidebarGroupContent>
              <motion.div variants={sidebarVariants} initial="hidden" animate="visible">
                <SidebarMenu>
                  {visibleNav.map((item) =>
                    // The stagger wrapper lives inside each <li> so the <ul> keeps only <li>
                    // children — a <div> between them breaks the list semantics (DEV-4142).
                    item.children !== undefined ? (
                      <CollapsibleNavItem
                        key={item.to}
                        item={item}
                        pathname={location.pathname}
                        onNavigate={navTo}
                      />
                    ) : (
                      <SidebarMenuItem key={item.to}>
                        <motion.div variants={sidebarItemVariants}>
                          <SidebarMenuButton
                            isActive={
                              item.to === '/dashboard'
                                ? location.pathname === '/dashboard'
                                : location.pathname.startsWith(item.to)
                            }
                            tooltip={t(ROUTE_I18N_KEYS[item.to] ?? item.to)}
                            onClick={() => {
                              navTo(item.to);
                            }}
                          >
                            <item.icon />
                            <span>{t(ROUTE_I18N_KEYS[item.to] ?? item.to)}</span>
                          </SidebarMenuButton>
                        </motion.div>
                      </SidebarMenuItem>
                    ),
                  )}
                </SidebarMenu>
              </motion.div>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        {/* ── Footer: copyright, then a divider, then the user menu ─────
            Per design the separator sits BELOW the copyright line. */}
        <SidebarFooter className="p-2 gap-1 shrink-0">
          <p className="py-1 text-center text-[11px] text-muted-foreground group-data-[collapsible=icon]:hidden">
            {t('sidebar.copyright', 'Copyright © Investbanq LLP 2026')}
          </p>
          <div className="border-t border-sidebar-border pt-1 group-data-[collapsible=icon]:border-t-0">
            <SidebarUserMenu />
          </div>
        </SidebarFooter>

        <SidebarRail />
      </Sidebar>

      {/* ── Main content area ─────────────────────────────────────────── */}
      <SidebarInset className="min-w-0">
        <ImpersonationBanner />
        {/* Tabs bar — acts as header, hidden when no tabs */}
        <TabsBar onNavigate={navTo} aiTrigger={aiTrigger} />

        {/* Page content reserves only the panel's DEFAULT width (docked); any
            width the user drags past that, up to the 1/3 cap, overlays on top
            instead of pushing the page further. The page stays fully
            interactive while the panel is open (no Focus Mode / inert). */}
        <div
          className="flex min-h-0 flex-1 flex-col transition-[margin] duration-300 ease-out"
          style={{ marginInlineEnd: aiReserve || undefined }}
        >
          <Suspense fallback={<PageSkeleton />}>
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={pageTransition}
                className="h-full"
              >
                <ModuleErrorBoundary>
                  <Outlet />
                </ModuleErrorBoundary>
              </motion.div>
            </AnimatePresence>
          </Suspense>
        </div>
      </SidebarInset>

      {/* ── Global overlays ───────────────────────────────────────────── */}
      <SearchModal onNavigate={navTo} />
      {!onAiCioMainPage && aiPanel}
    </SidebarProvider>
  );
}
